<script>
import axios from '@/utils/axios';

export default {
  inject: ['setLoading'],
  data() {
    return {
      announcement: null,   // { id, title }
      stats: null,
      loading: false,
    };
  },
  computed: {
    readPercent() {
      if (!this.stats || !this.stats.total_employees) return 0;
      return Math.round((this.stats.read_count / this.stats.total_employees) * 100);
    },
  },
  methods: {
    open(announcement) {
      this.announcement = announcement;
      this.stats = null;
      this.fetchStats();
    },

    async fetchStats() {
      if (!this.announcement) return;
      this.loading = true;
      try {
        const res = await axios.get(`/hrms/announcements/${this.announcement.id}/read-stats`);
        this.stats = res.data.data;
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.loading = false;
      }
    },

    initials(name) {
      if (!name) return '?';
      const parts = name.trim().split(/\s+/);
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    },
  },
};
</script>

<template>
  <div class="modal fade" id="hrm_announcement_stats_modal">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">
            Read Stats
            <span v-if="announcement" style="font-size:13px; color:#94a3b8; font-weight:500;">— {{ announcement.title }}</span>
          </h4>
          <button type="button" class="btn-close custom-btn-close" data-bs-dismiss="modal" aria-label="Close">
            <i class="ti ti-x"></i>
          </button>
        </div>

        <div class="modal-body">

          <div v-if="loading" class="text-center py-4 text-muted">
            <div class="spinner-border spinner-border-sm text-primary mb-2"></div>
            <div>Loading stats...</div>
          </div>

          <template v-else-if="stats">
            <!-- summary row -->
            <div class="row g-2 mb-4">
              <div class="col-4">
                <div class="rs-summary-card">
                  <div class="rs-summary-num">{{ stats.total_employees }}</div>
                  <div class="rs-summary-label">Total Employees</div>
                </div>
              </div>
              <div class="col-4">
                <div class="rs-summary-card rs-read">
                  <div class="rs-summary-num">{{ stats.read_count }}</div>
                  <div class="rs-summary-label">Read</div>
                </div>
              </div>
              <div class="col-4">
                <div class="rs-summary-card rs-unread">
                  <div class="rs-summary-num">{{ stats.unread_count }}</div>
                  <div class="rs-summary-label">Unread</div>
                </div>
              </div>
            </div>

            <!-- progress bar -->
            <div class="mb-4">
              <div class="d-flex justify-content-between mb-1">
                <span style="font-size:11.5px; color:#64748b;">Read Progress</span>
                <span style="font-size:11.5px; font-weight:700; color:#0f172a;">{{ readPercent }}%</span>
              </div>
              <div class="progress" style="height:8px; border-radius:6px;">
                <div class="progress-bar bg-success" :style="{ width: readPercent + '%' }"></div>
              </div>
            </div>

            <!-- unread employees list -->
            <h6 class="mb-2" style="font-size:12.5px; font-weight:700; color:#475569;">
              Unread By ({{ stats.unread_employees?.length || 0 }})
            </h6>
            <div v-if="!stats.unread_employees?.length" class="text-center py-3 text-muted" style="font-size:12.5px;">
              Everyone has read this announcement.
            </div>
            <div v-else class="rs-emp-list">
              <div v-for="emp in stats.unread_employees" :key="emp.id" class="rs-emp-row">
                <div class="rs-emp-avatar">
                  <img v-if="emp.image" :src="emp.image" :alt="emp.name" />
                  <span v-else>{{ initials(emp.name) }}</span>
                </div>
                <div class="rs-emp-info">
                  <div class="rs-emp-name">{{ emp.name }}</div>
                  <div class="rs-emp-email">{{ emp.email }}</div>
                </div>
              </div>
            </div>
          </template>

        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-light" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.rs-summary-card {
  text-align: center; padding: 14px 8px;
  background: #f8fafc; border-radius: 10px;
}
.rs-summary-card.rs-read { background: #f0fdf4; }
.rs-summary-card.rs-unread { background: #fef2f2; }
.rs-summary-num { font-size: 20px; font-weight: 800; color: #0f172a; }
.rs-summary-card.rs-read .rs-summary-num { color: #16a34a; }
.rs-summary-card.rs-unread .rs-summary-num { color: #dc2626; }
.rs-summary-label { font-size: 10.5px; color: #94a3b8; font-weight: 600; margin-top: 2px; }

.rs-emp-list {
  display: flex; flex-direction: column; gap: 8px;
  max-height: 260px; overflow-y: auto;
}
.rs-emp-row {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 10px; border: 1px solid #f1f5f9; border-radius: 10px;
}
.rs-emp-avatar {
  width: 34px; height: 34px; border-radius: 50%;
  background: #6366f1; color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; flex-shrink: 0; overflow: hidden;
}
.rs-emp-avatar img { width: 100%; height: 100%; object-fit: cover; }
.rs-emp-info { min-width: 0; }
.rs-emp-name { font-size: 12.5px; font-weight: 600; color: #1e293b; }
.rs-emp-email { font-size: 11px; color: #94a3b8; }
</style>